import { useState } from "react";
import { getFontFamily } from "../constants/colors";
import { getCurrentTier, TIERS, activateCode } from "../subscription";

/*
 * ProPage — full-screen pricing page
 * 
 * Props:
 *   onClose: () => void (go back)
 *   onSubscribe: (tier) => void (handle payment — for now just activates via code)
 *   lang: "en" | "hi"
 *   currentTier: "free" | "lite" | "pro"
 */

const CheckIcon = ({ color = "#1A7A52", size = 14 }) => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", background: color === "#1A7A52" ? "#E8F5EE" : "#E4F0F6", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <svg width={size - 2} height={size - 2} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
  </div>
);
const CrossIcon = () => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#FEE8E8", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#CC4444" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
  </div>
);

const FEATURES = [
  { key: "learn", en: "Learn mode", hi: "लर्न मोड", sub_en: "All chapters, slides, visuals", sub_hi: "सभी chapters, slides, visuals", free: "check", lite: "check", pro: "check" },
  { key: "bites", en: "Bites", hi: "बाइट्स", sub_en: "Chapter stories", sub_hi: "Chapter stories", free: "check", lite: "check", pro: "check" },
  { key: "flash", en: "Flash cards", hi: "फ़्लैश कार्ड", sub_en: "Quick revision", sub_hi: "Quick revision", free: "3 chapters", lite: "Full", pro: "Full" },
  { key: "tests", en: "Board-style tests", hi: "बोर्ड-स्टाइल tests", sub_en: "Exam corner + mocks", sub_hi: "Exam corner + mocks", free: "cross", lite: "Unlimited", pro: "Unlimited" },
  { key: "chat", en: "AI tutor chat", hi: "AI tutor chat", sub_en: "Socratic guidance", sub_hi: "Socratic guidance", free: "cross", lite: "20/mo", pro: "Unlimited*" },
  { key: "practice", en: "Practice solving", hi: "Practice solving", sub_en: "Step-by-step hints", sub_hi: "Step-by-step hints", free: "cross", lite: "20/mo", pro: "Unlimited*" },
];

export function ProPage({ onClose, onSubscribe, lang = "en" }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const currentTier = getCurrentTier();
  const [codeInput, setCodeInput] = useState("");
  const [codeMsg, setCodeMsg] = useState(null);
  // BETA-PRICING-HIDDEN-APPLIED: selectedPlan + plan-selection logic removed —
  // tier cards are non-interactive while pricing is hidden.

  const handleCodeSubmit = () => {
    const result = activateCode(codeInput);
    if (result.success) {
      setCodeMsg({ type: "success", text: isHi ? `${result.tierInfo.name} activated!` : `${result.tierInfo.name} plan activated!` });
      setTimeout(() => onClose?.(), 1500);
    } else {
      setCodeMsg({ type: "error", text: isHi ? "गलत कोड" : "Invalid code" });
    }
  };

  const renderCell = (val, col) => {
    if (val === "check") return <CheckIcon color={col === "pro" ? "#1A7A52" : col === "lite" ? "#2A6A8A" : "#1A7A52"} />;
    if (val === "cross") return <CrossIcon />;
    return <span style={{ fontSize: 11, fontWeight: 700, color: col === "pro" ? "#1A7A52" : col === "lite" ? "#2A6A8A" : "#6A808C" }}>{val}</span>;
  };

  return (
    <div style={{
      minHeight: "100dvh",
      background: "linear-gradient(180deg, #6AAAC8 0%, #84BEDA 10%, #A0D0E6 20%, #BAE0F0 32%, #D2ECF5 44%, #E4F1F8 56%, #EEF6FA 68%, #F5F9FB 80%, #F9FBFC 100%)",
      fontFamily: ff,
      paddingBottom: 100,
    }}>

      {/* Close button */}
      <div style={{ padding: "16px 16px 0", display: "flex", justifyContent: "flex-end" }}>
        <div onClick={onClose} style={{
          width: 34, height: 34, borderRadius: "50%",
          background: "rgba(255,255,255,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
      </div>

      {/* BETA-PRICING-HIDDEN-APPLIED: beta banner — pricing is intentionally hidden,
          tiers shown as "Coming soon" so users see the planned shape but feel no upgrade pressure. */}
      <div style={{
        margin: "8px 16px 0", padding: "10px 14px",
        background: "rgba(26,122,82,0.10)", border: "1px solid rgba(26,122,82,0.25)",
        borderRadius: 12, textAlign: "center",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0A4A30", letterSpacing: "0.02em" }}>
          {isHi ? "🎉 बीटा के दौरान सब कुछ मुफ्त" : "🎉 Free during beta"}
        </div>
        <div style={{ fontSize: 11, color: "#1A7A52", marginTop: 2 }}>
          {isHi ? "प्राइसिंग जल्द आ रही है" : "Pricing coming soon"}
        </div>
      </div>

      {/* Hero */}
      <div style={{ textAlign: "center", padding: "16px 24px 24px" }}>
        <div style={{
          width: 52, height: 52, background: "#1A7A52", borderRadius: 16,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(26,122,82,0.35)",
          marginBottom: 14,
        }}>
          <span style={{ fontFamily: "Georgia,serif", fontSize: 28, fontWeight: 600, color: "#9FE1CB" }}>S.</span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#0A1820", letterSpacing: "-0.03em" }}>
          Samajh <span style={{ color: "#1A7A52" }}>Pro</span>
        </div>
        <div style={{ fontSize: 13, color: "#3A5464", marginTop: 6, lineHeight: 1.5 }}>
          {isHi ? "आपके बच्चे का personal AI tutor" : "Your child's personal AI tutor"}
        </div>
      </div>

      {/* BETA-PRICING-HIDDEN-APPLIED: tier cards still shown so users see the planned shape,
          but prices replaced with "Coming soon" pill. Cards no longer selectable / pre-highlighted. */}
      <div style={{ padding: "0 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
        <div style={{
          background: "white", borderRadius: 16, padding: "16px 12px", textAlign: "center",
          border: "1.5px solid #D8E4EE",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          opacity: 0.85,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#2A6A8A", textTransform: "uppercase", letterSpacing: "0.05em" }}>Lite</div>
          <div style={{
            display: "inline-block", marginTop: 10, padding: "5px 10px",
            background: "#EEF4F8", color: "#4A6474",
            fontSize: 11, fontWeight: 700, borderRadius: 999,
            letterSpacing: "0.02em",
          }}>{isHi ? "जल्द आ रहा है" : "Coming soon"}</div>
          <div style={{ fontSize: 10, color: "#6A808C", marginTop: 8 }}>{isHi ? "Exam prep + AI" : "Exam prep + AI"}</div>
        </div>
        <div style={{
          background: "white", borderRadius: 16, padding: "16px 12px", textAlign: "center",
          border: "1.5px solid #D8E4EE", position: "relative",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          opacity: 0.85,
        }}>
          <div style={{ position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)", background: "#1A7A52", color: "white", padding: "2px 10px", borderRadius: 6, fontSize: 7, fontWeight: 700, letterSpacing: "0.05em" }}>RECOMMENDED</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#1A7A52", textTransform: "uppercase", letterSpacing: "0.05em" }}>Pro</div>
          <div style={{
            display: "inline-block", marginTop: 10, padding: "5px 10px",
            background: "#E8F5EE", color: "#1A7A52",
            fontSize: 11, fontWeight: 700, borderRadius: 999,
            letterSpacing: "0.02em",
          }}>{isHi ? "जल्द आ रहा है" : "Coming soon"}</div>
          <div style={{ fontSize: 10, color: "#6A808C", marginTop: 8 }}>{isHi ? "Full AI tutor" : "Full AI tutor"}</div>
        </div>
      </div>

      {/* Feature comparison */}
      <div style={{
        margin: "0 16px", background: "white", borderRadius: 20,
        border: "1px solid #D8E4EE",
        // WebView-compensated
        boxShadow: "0 2px 8px rgba(15,31,42,0.14), 0 8px 24px rgba(15,31,42,0.08)",
        overflow: "hidden",
      }}>
        {/* Table header */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 52px 52px 52px",
          padding: "14px 16px 10px", borderBottom: "2px solid #E8EFF5",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#0A1820" }}>{isHi ? "सुविधा" : "Benefits"}</div>
          <div style={{ textAlign: "center", fontSize: 9, fontWeight: 600, color: "#6A808C" }}>Free</div>
          <div style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "#2A6A8A", background: "rgba(42,106,138,0.06)", borderRadius: 4, padding: "2px 0" }}>Lite</div>
          <div style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "#1A7A52", background: "rgba(26,122,82,0.06)", borderRadius: 4, padding: "2px 0" }}>Pro</div>
        </div>

        {/* Rows */}
        {FEATURES.map((f, i) => (
          <div key={f.key} style={{
            display: "grid", gridTemplateColumns: "1fr 52px 52px 52px",
            padding: "12px 16px", alignItems: "center",
            borderBottom: i < FEATURES.length - 1 ? "1px solid #F0F4F8" : "none",
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#0A1820" }}>{isHi ? f.hi : f.en}</div>
              <div style={{ fontSize: 9, color: "#6A808C", marginTop: 1 }}>{isHi ? f.sub_hi : f.sub_en}</div>
            </div>
            <div style={{ textAlign: "center" }}>{renderCell(f.free, "free")}</div>
            <div style={{ textAlign: "center" }}>{renderCell(f.lite, "lite")}</div>
            <div style={{ textAlign: "center" }}>{renderCell(f.pro, "pro")}</div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "right", padding: "4px 20px 0", fontSize: 9, color: "#6A808C" }}>*{isHi ? "fair usage ~80/mo" : "fair usage ~80/mo"}</div>

      {/* Tuition comparison */}
      <div style={{
        margin: "16px 16px 0",
        background: "linear-gradient(135deg, #FFF8E8, #FDF0D0)",
        borderRadius: 14, padding: "14px 16px",
        border: "1px solid #E8D8A8",
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#7A5C00", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          {isHi ? "तुलना करें" : "Compare with tuition"}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 10, color: "#9A8020" }}>{isHi ? "प्राइवेट tutor" : "Private tutor"}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#7A5C00", marginTop: 2 }}>₹2,000+</div>
            <div style={{ fontSize: 8, color: "#9A8020" }}>{isHi ? "प्रति subject/mo" : "per subject/mo"}</div>
          </div>
          <div style={{ width: 1, height: 36, background: "rgba(200,150,10,0.2)" }} />
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 10, color: "#9A8020" }}>{isHi ? "Coaching class" : "Coaching class"}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#7A5C00", marginTop: 2 }}>₹5,000+</div>
            <div style={{ fontSize: 8, color: "#9A8020" }}>{isHi ? "प्रति month" : "per month"}</div>
          </div>
          <div style={{ width: 1, height: 36, background: "rgba(200,150,10,0.2)" }} />
          <div style={{ textAlign: "center", flex: 1 }}>
            <div style={{ fontSize: 10, color: "#1A7A52", fontWeight: 600 }}>Samajh</div>
            {/* BETA-PRICING-HIDDEN-APPLIED: ₹109 → Free during beta */}
            <div style={{ fontSize: 14, fontWeight: 800, color: "#1A7A52", marginTop: 2, lineHeight: 1.15 }}>{isHi ? "मुफ्त" : "Free"}</div>
            <div style={{ fontSize: 8, color: "#1A7A52", fontWeight: 600 }}>{isHi ? "बीटा के दौरान" : "during beta"}</div>
          </div>
        </div>
      </div>

      {/* Trust signals */}
      <div style={{ margin: "16px 16px 0", display: "flex", flexDirection: "column", gap: 8 }}>
        {[
          { icon: "🔒", text: isHi ? "कभी भी cancel करें · कोई सवाल नहीं" : "Cancel anytime · No questions asked" },
          { icon: "💳", text: isHi ? "UPI, cards & wallets · Secure payment" : "UPI, cards & wallets · Secure payment" },
          { icon: "🇮🇳", text: "CBSE · Class 10 · Hindi & English" },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14 }}>{item.icon}</span>
            <span style={{ fontSize: 11, color: "#3A5464" }}>{item.text}</span>
          </div>
        ))}
      </div>

      {/* Code entry */}
      <div style={{
        margin: "20px 16px 0", background: "#F5F9FB", borderRadius: 14,
        padding: "14px 16px", border: "1px solid #E6EEF5",
      }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#3A5464", marginBottom: 8 }}>
          {isHi ? "कोड है? यहाँ डालें" : "Have a code? Enter here"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            value={codeInput}
            onChange={(e) => { setCodeInput(e.target.value.toUpperCase()); setCodeMsg(null); }}
            placeholder={isHi ? "कोड डालें" : "Enter code"}
            style={{
              flex: 1, padding: "10px 14px", borderRadius: 10,
              border: "1px solid #D8E4EE", fontSize: 14, fontWeight: 600,
              fontFamily: ff, color: "#0A1820", background: "white",
              outline: "none", letterSpacing: "0.05em",
            }}
          />
          <button
            onClick={handleCodeSubmit}
            disabled={!codeInput.trim()}
            style={{
              padding: "10px 18px", borderRadius: 10,
              background: codeInput.trim() ? "#1A7A52" : "#D8E4EE",
              color: codeInput.trim() ? "white" : "#8A9AA4",
              border: "none", fontSize: 13, fontWeight: 700,
              cursor: codeInput.trim() ? "pointer" : "default",
              fontFamily: ff,
            }}
          >
            {isHi ? "लागू" : "Apply"}
          </button>
        </div>
        {codeMsg && (
          <div style={{
            marginTop: 8, fontSize: 12, fontWeight: 600,
            color: codeMsg.type === "success" ? "#1A7A52" : "#CC4444",
          }}>
            {codeMsg.type === "success" ? "✓ " : "✗ "}{codeMsg.text}
          </div>
        )}
      </div>

      {/* Current tier badge */}
      {currentTier !== "free" && (
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "#1A7A52", fontWeight: 600 }}>
          ✓ {isHi ? "आपका प्लान:" : "Your current plan:"} {TIERS[currentTier]?.name}
        </div>
      )}

      {/* BETA-PRICING-HIDDEN-APPLIED: subscribe CTA replaced with disabled "Coming soon" state.
          Money-back guarantee line hidden — nothing to refund during free beta. */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        padding: "12px 16px 24px",
        background: "linear-gradient(180deg, transparent, #F5F9FB 30%)",
        zIndex: 10,
      }}>
        <button
          disabled
          style={{
            width: "100%", padding: "16px", border: "none",
            borderRadius: 16, fontSize: 16, fontWeight: 800,
            cursor: "default", fontFamily: ff,
            background: "#D8E4EE", color: "#6A808C",
            letterSpacing: "-0.02em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          <span>{isHi ? "जल्द आ रहा है" : "Coming soon"}</span>
        </button>
        <div style={{ textAlign: "center", marginTop: 6, fontSize: 10, color: "#6A808C" }}>
          {isHi ? "बीटा के दौरान सब कुछ मुफ्त है" : "Everything is free during beta"}
        </div>
      </div>
    </div>
  );
}
